import { useState } from "react";
import { Link } from "@/lib/router";
import { useRouter } from "@/lib/router";
import { createClient } from "@/lib/supabase/client";
import { authErrorMessage } from "@/lib/auth/messages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/** 이메일+비밀번호 로그인 폼. 탈퇴 완료·인증 실패 같은 안내는 페이지가 폼 위에 따로 보여준다. */
export function LoginForm() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const supabase = createClient();
    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      setError(authErrorMessage(error));
      setIsLoading(false);
      return;
    }

    // 서버 컴포넌트가 새 세션 쿠키를 읽도록 새로고침하며 이동한다.
    router.push("/");
    router.refresh();
  }

  return (
    <Card className="w-full max-w-sm border-0 bg-transparent shadow-none ring-0">
      <CardHeader className="items-center text-center">
        <CardTitle className="text-2xl">로그인</CardTitle>
        <CardDescription>이메일과 비밀번호로 로그인하세요</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="email">이메일</Label>
            <Input
              className="h-11"
              id="email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="password">비밀번호</Label>
            <Input
              className="h-11"
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <Button type="submit" disabled={isLoading} className="h-11 w-full rounded-full">
            {isLoading ? "로그인 중..." : "로그인"}
          </Button>

          <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-sm text-muted-foreground">
            <span>
              계정이 없으신가요?{" "}
              <Link href="/auth/sign-up" className="text-foreground underline underline-offset-4">
                회원가입
              </Link>
            </span>
            <Link href="/auth/forgot-password" className="text-xs underline underline-offset-4">
              비밀번호를 잊으셨나요?
            </Link>
          </p>
          <p className="text-center text-xs text-muted-foreground">
            <Link href="/terms" className="underline underline-offset-4">이용약관</Link>
            {" · "}
            <Link href="/privacy" className="underline underline-offset-4">개인정보 처리방침</Link>
            {" · "}
            <Link href="/scripture-copyright" className="underline underline-offset-4">성경 본문 저작권</Link>
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
